import { useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import "./Avatar.css";

/*
 * Foto de perfil de un usuario.
 *
 * La ruta pide sesión, así que no se puede poner en un src: hay que bajarla
 * como blob. Para que una lista de veinte clientes no haga veinte descargas
 * repetidas en cada repintado, las URLs se guardan en una caché de módulo:
 * una petición por usuario y por sesión del navegador.
 *
 * Si no hay foto, se dibujan las iniciales sobre un color derivado del id.
 * Siempre sale algo: un hueco gris en una lista de personas queda fatal.
 */

const cache = new Map(); // id -> url de blob | "no" | Promise

const TONOS = [
  ["#1f6f4a", "#d8f2e4"],
  ["#1d4e6f", "#d6e9f5"],
  ["#6f4a1f", "#f5e6d2"],
  ["#4a1f6f", "#e9dcf5"],
  ["#6f1f3d", "#f5d9e3"],
  ["#2f5f1f", "#e2f2d4"],
  ["#1f5f5f", "#d4f0f0"],
  ["#5f3a1f", "#f2e3d2"],
];

const tono = (clave = "") => {
  let n = 0;
  for (let i = 0; i < clave.length; i += 1) n = (n * 31 + clave.charCodeAt(i)) % 9973;
  return TONOS[n % TONOS.length];
};

export const iniciales = (nombre = "", apellido = "") => {
  const a = (nombre || "").trim()[0] || "";
  const b = (apellido || "").trim()[0] || "";
  return (a + b).toUpperCase() || "·";
};

async function bajar(id) {
  if (cache.has(id)) return cache.get(id);
  const promesa = (async () => {
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/users/${id}/avatar`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      cache.set(id, url);
      return url;
    } catch {
      cache.set(id, "no");
      return "no";
    }
  })();
  cache.set(id, promesa);
  return promesa;
}

// Para que, al cambiar la propia foto, se vuelva a pedir
export const olvidarAvatar = (id) => {
  const previo = cache.get(id);
  if (typeof previo === "string" && previo !== "no") URL.revokeObjectURL(previo);
  cache.delete(id);
};

function Avatar({ userId, nombre, apellido, tamano = 42, className = "", titulo }) {
  const [url, setUrl] = useState(() => {
    const c = cache.get(userId);
    return typeof c === "string" && c !== "no" ? c : null;
  });

  useEffect(() => {
    if (!userId) return;
    let vivo = true;
    bajar(userId).then((u) => {
      if (vivo) setUrl(u === "no" ? null : u);
    });
    return () => {
      vivo = false;
    };
  }, [userId]);

  const [texto, fondo] = tono(String(userId || nombre || ""));
  const letras = iniciales(nombre, apellido);

  return (
    <span
      className={`av ${className}`}
      style={{
        "--av-size": `${tamano}px`,
        "--av-bg": fondo,
        "--av-fg": texto,
        "--av-fs": `${Math.round(tamano * 0.4)}px`,
      }}
      title={titulo}
      aria-hidden={!titulo}
    >
      {url ? <img src={url} alt={titulo || ""} /> : <span className="av-letras">{letras}</span>}
    </span>
  );
}

export default Avatar;
